'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Scrollbar } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/scrollbar';
import Image from 'next/image';
import { Heading } from '@/components/ui/Heading';
import Link from 'next/link';
import { SwiperListProps } from '@/core/models/slider-model';
import { useDictionary } from '@/core/hooks/use-dictionary';
import CustomButton from './CustomButton';

// وقتی آیتم‌ها کمن، عرض هر اسلاید بزرگ‌تر می‌شه تا ردیف رو مساوی پر کنه؛ ولی
// اگه جا نباشه (زوم بالا، صفحه‌ی باریک) به‌جای شکستن خط (که با CSS grid پیش
// می‌اومد)، به حداقل‌عرض می‌چسبه و خودِ سوییپر اسکرول‌پذیرش می‌کنه.
const MAX_VISIBLE = 5;
const MIN_ITEM_WIDTH = 240;
const GAP = 16;

export default function SwiperList({ data }: SwiperListProps) {
  const { dictionary } = useDictionary();

  const items = Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.sliderItems)
      ? data.sliderItems
      : [];

  const visibleCount = Math.min(items.length, MAX_VISIBLE) || 1;
  const slideWidth = `max(${MIN_ITEM_WIDTH}px, calc((100% - ${(visibleCount - 1) * GAP}px) / ${visibleCount}))`;

  const renderCard = (item: (typeof items)[number]) => (
    <div className='relative overflow-hidden rounded-md aspect-[5/6]'>
      {(item.image?.url ||
        item.image_info?.url) && (
          <Image
            src={
              item.image?.url ||
              item.image_info?.url
            }
            alt={item.title || ''}
            fill
            className="object-cover rounded-md"
          />
        )}
      <div className="absolute inset-0 z-0 bg-linear-to-t rounded-md from-black/40 to-white/0" />
      <div className='absolute bottom-3 right-3 left-3'>
        <Heading className='text-primary-0' level={4}>{item.title}</Heading>
        <p className="text-primary-100">{item.description}</p>
      </div>
    </div>
  );

  return (
    <div className="w-full col-span-12">
      <div className="flex justify-between items-center mb-10 max-md:flex-col max-md:items-start gap-4">
        <div>
          <Heading level={3}>{data.title}</Heading>
          <p className="text-primary-600">{data.description}</p>
        </div>
        {data.link && (
          <CustomButton>
            <Link href={data.link}>
              {dictionary.ui?.pages.seeAll}
            </Link>
          </CustomButton>
        )}
      </div>
      <Swiper
        modules={[Pagination]}
        spaceBetween={GAP}
        slidesPerView="auto"
        pagination={{ clickable: true }}
        className="w-full"
        style={{ paddingBottom: '50px' }}
      >
        {items.map((item, index) => (
          <SwiperSlide
            key={index}
            style={{ width: slideWidth, height: '360px' }}
          >
            {item.link ? <Link href={item.link}>{renderCard(item)}</Link> : renderCard(item)}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
